/* Newest home products: Shopify sorts the collection, then we merge available results.
   No Admin API, collection mutation, product reconstruction or persistent product cache. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && !root.CollinoNewestProducts) root.CollinoNewestProducts = api;
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  let queue = Promise.resolve();
  let rateLimited = false;

  function buildURL(source, sectionId, page, locationHref) {
    const location = new URL(locationHref);
    const url = new URL(source.url, location);
    if (url.origin !== location.origin || !/\/collections\/[^/]+\/?$/.test(url.pathname)) {
      throw new Error('Invalid collection URL');
    }
    url.search = '';
    url.hash = '';
    url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('view', 'home-newest');
    url.searchParams.set('page', String(page));
    // Keep explicit draft context if present; preview hosts/cookies also stay same-origin.
    const preview = location.searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const country = location.searchParams.get('country');
    if (country && /^[A-Z]{2}$/i.test(country)) url.searchParams.set('country', country.toUpperCase());
    return url.href;
  }

  async function collectNewest(sources, limit, readPage, signal) {
    if (!Number.isInteger(limit) || limit < 1 || limit > 50 || !sources.length) {
      throw new Error('Invalid newest configuration');
    }
    const merged = new Map();
    const uniqueSources = [...new Map(sources.map(source => [source.handle, source])).values()];
    for (const [sourceIndex, source] of uniqueSources.entries()) {
      const found = new Map();
      let previousDate = Infinity;
      let page = 1;
      while (true) {
        signal?.throwIfAborted();
        // A budget failure never commits a partial "newest" result.
        if (page > 20) throw new Error('Newest query exceeded page budget');
        const result = await readPage(source, page);
        signal?.throwIfAborted();
        if (!Number.isInteger(result.pages) || result.pages < 1 ||
            result.page !== page || result.pages < page ||
            !Number.isInteger(result.total) || result.total < 0) {
          throw new Error('Invalid collection pagination');
        }
        for (const item of result.items) {
          if (!/^\d+$/.test(item.id) || !Number.isFinite(item.created) ||
              item.created <= 0 || item.created > previousDate) {
            throw new Error('Collection response is not creation-descending');
          }
          previousDate = item.created;
          if (!found.has(item.id)) found.set(item.id, { ...item, sourceIndex, rank: found.size });
          if (found.size >= limit) break;
        }
        if (found.size >= limit) break;
        if (page >= result.pages) {
          if (result.total > 25000) throw new Error('Collection exceeds Shopify pagination');
          break;
        }
        page += 1;
      }
      // Top N from EACH collection suffice for top N of their deduplicated union.
      // A product omitted from a source already has N newer candidates in that source.
      for (const [id, item] of found) if (!merged.has(id)) merged.set(id, item);
    }
    return [...merged.values()]
      .sort((a, b) => b.created - a.created || a.sourceIndex - b.sourceIndex || a.rank - b.rank)
      .slice(0, limit);
  }

  function parseFeed(html, expected, Parser) {
    if (typeof html !== 'string') throw new Error('Missing section response');
    const doc = new Parser().parseFromString(html, 'text/html');
    const feeds = doc.querySelectorAll('[data-featured-feed]');
    if (feeds.length !== 1) throw new Error('Missing collection feed');
    const feed = feeds[0];
    if (feed.dataset.sectionId !== expected.sectionId ||
        feed.dataset.handle !== expected.handle || feed.dataset.sort !== 'created-descending' ||
        feed.dataset.country !== expected.country || feed.dataset.currency !== expected.currency) {
      throw new Error('Collection context mismatch');
    }
    const items = [...feed.querySelectorAll('[data-featured-entry]')].map(entry => {
      const card = entry.querySelector('.featured-product-slide');
      if (!card || card.tagName.toLowerCase() !== 'div' || card.dataset.productId !== entry.dataset.id ||
          card.querySelector('script, iframe, object, embed, style, link, meta, base')) {
        throw new Error('Invalid product card');
      }
      // Import only our rendered card subtree, never scripts/handlers from a response.
      for (const node of [card, ...card.querySelectorAll('*')]) {
        for (const attribute of node.attributes) {
          if (/^on/i.test(attribute.name) || attribute.name === 'srcdoc' ||
              (/^(href|src|action|formaction|xlink:href)$/i.test(attribute.name) &&
                /^(javascript|data|vbscript):/i.test(attribute.value.replace(/[\u0000-\u0020]/g, '')))) {
            throw new Error('Unsafe product card');
          }
        }
      }
      return { id: entry.dataset.id, created: Number(entry.dataset.created), card };
    });
    return {
      items,
      page: Number(feed.dataset.page),
      pages: Math.max(1, Number(feed.dataset.pages)),
      total: Number(feed.dataset.total)
    };
  }

  function enqueue(job, signal) {
    const next = queue.then(() => {
      signal.throwIfAborted();
      if (rateLimited) throw new Error('Shopify rate limit; no automatic retry');
      return job();
    });
    queue = next.catch(() => {});
    return next;
  }

  function applyCardSettings(card, settings) {
    const optional = { showVendor: '.featured-product-card__vendor', showSku: '.featured-product-card__sku',
      showCollectionTag: '.featured-product-card__collection-tag', showFavIcon: '.featured-product-card__fav',
      showTransfer: '.featured-product-card__transfer', showFreeShipping: '.featured-product-card__shipping' };
    for (const [flag, selector] of Object.entries(optional)) {
      if (settings[flag] !== 'true') card.querySelectorAll(selector).forEach(node => node.remove());
    }
    if (settings.showTransfer !== 'true') card.querySelector('.featured-product-card__prices')?.classList.remove('featured-product-card__prices--has-transfer');
    const consult = card.querySelector('.price--consult');
    if (consult) {
      consult.textContent = settings.consultLabel;
      if (!/wa\.me|whatsapp/.test(settings.consultUrl || '')) {
        consult.setAttribute('href', settings.consultUrl || '/pages/informacion');
        consult.removeAttribute('target');
        consult.removeAttribute('rel');
      }
    }
  }

  function attach(slider) {
    const section = slider.section;
    if (slider.newestController || section.dataset.orderState === 'ready') return;
    const controller = new AbortController();
    slider.newestController = controller;
    const signal = controller.signal;
    const status = section.querySelector('[data-newest-status]');
    const sourceLinks = section.querySelectorAll('[data-newest-sources] a');
    const sources = [...sourceLinks].map(link => ({ url: link.getAttribute('href'), handle: link.dataset.handle }));
    const expected = {
      sectionId: section.dataset.feedSection,
      country: section.dataset.country,
      currency: section.dataset.currency
    };
    const limit = Number(section.dataset.productsLimit);
    let observer;
    let timer;
    let started = false;
    let applied = false;
    let cancelledByUser = false;
    const wasInert = slider.wrapper.inert;
    const ownsSection = () => slider.wrapper.featuredProductSlider === slider;

    function fallback() {
      if (applied || !started || !ownsSection()) return;
      section.dataset.orderState = 'fallback';
      section.removeAttribute('aria-busy');
      slider.wrapper.inert = wasInert;
      if (status) {
        if (cancelledByUser) { status.hidden = true; return; }
        const hasPreviousCards = !!slider.track.querySelector('.featured-product-slide');
        status.textContent = hasPreviousCards ? status.dataset.error : (status.dataset.errorEmpty || status.dataset.error);
        status.hidden = false;
      }
    }
    signal.addEventListener('abort', () => {
      observer?.disconnect();
      clearTimeout(timer);
      fallback();
    }, { once: true });

    // The server-rendered cards remain usable while the newest query runs.
    // If a shopper begins using them, keep that selection stable for this visit.
    const keepCurrentCards = () => {
      if (!started || applied || signal.aborted) return;
      cancelledByUser = true;
      controller.abort();
    };
    slider.wrapper.addEventListener('pointerdown', keepCurrentCards, { signal });
    slider.wrapper.addEventListener('focusin', keepCurrentCards, { signal });

    async function start() {
      if (started || signal.aborted) return;
      started = true;
      observer?.disconnect();
      // Do not replace a card somebody already focused/clicked before initialization.
      if (slider.wrapper.contains(document.activeElement)) {
        fallback();
        return;
      }
      section.dataset.orderState = 'loading';
      const hasCurrentCards = !!slider.track.querySelector('.featured-product-slide');
      if (hasCurrentCards) section.removeAttribute('aria-busy');
      else section.setAttribute('aria-busy', 'true');
      if (status) {
        status.textContent = status.dataset.loading;
        status.hidden = hasCurrentCards;
      }
      timer = setTimeout(() => controller.abort(), 30000);
      try {
        const items = await collectNewest(sources, limit, async (source, page) => {
          const contextURL = new URL(window.location.href);
          if (window.Shopify?.theme?.role === 'unpublished') {
            contextURL.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
          }
          const url = buildURL(source, expected.sectionId, page, contextURL.href);
          return enqueue(async () => {
            const response = await fetch(url, {
              credentials: 'same-origin', signal, headers: { Accept: 'text/html' }
            });
            if (response.status === 429) rateLimited = true;
            if (!response.ok) throw new Error('Collection request failed: ' + response.status);
            return parseFeed(await response.text(), { ...expected, handle: source.handle }, DOMParser);
          }, signal);
        }, signal);
        signal.throwIfAborted();
        if (!section.isConnected || !ownsSection()) return;
        const fragment = document.createDocumentFragment();
        for (const item of items) {
          const card = document.importNode(item.card, true);
          applyCardSettings(card, section.dataset);
          card.setAttribute('data-product-created-at', new Date(item.created * 1000).toISOString());
          fragment.appendChild(card);
        }
        // One atomic commit: never mix a successful source with a failed/unfinished one.
        slider.track.replaceChildren(fragment);
        slider.slides = [...slider.track.querySelectorAll('.featured-product-slide')];
        slider.calc();
        slider.go(0, false);
        section.hidden = items.length === 0 && !window.Shopify?.designMode;
        section.dataset.orderState = 'ready';
        applied = true;
        if (status) status.hidden = true;
      } catch (error) {
        if (ownsSection()) section.dataset.orderError = error.message;
        fallback();
      } finally {
        clearTimeout(timer);
        if (ownsSection()) {
          section.removeAttribute('aria-busy');
          slider.wrapper.inert = wasInert;
        }
      }
    }
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) start();
      }, { rootMargin: '800px' });
      observer.observe(section);
    } else {
      start();
    }
  }

  return { buildURL, collectNewest, parseFeed, applyCardSettings, attach };
});
